import {
  applyRebase,
  buildPrefixLayout,
  clamp,
  heightOf,
  initialOriginForWindow,
  pickResizeAnchor,
  pickTopVisibleAnchor,
  planRunwayRebase,
  renderWindowPlanForViewport,
  solveAnchor,
  type AnchorSolveMode,
  type LayoutAnchor,
  type PrefixLayout,
  type RenderDirection
} from './layout';
import {
  DEFAULT_ITEM_HEIGHT_ESTIMATE_PX,
  HeightCache,
  resolveHeight,
  type ItemHeightEstimator
} from './heights';
import type {
  AnchorSnapshot,
  EngineDebugEvent,
  EngineDebugSnapshot,
  EdgeSide,
  EdgeState,
  EngineItem,
  EngineLocation,
  FeedEngine,
  FeedItemKey,
  FollowRule,
  RenderWindow,
  ScrollBehavior,
  ScrollState,
  Unsubscribe,
  ViewportInsets
} from './index';

const EDGE_ZONE_H = 128;
const RUNWAY_MIN = 1024;
const RUNWAY_TARGET = 4096;
const RUNWAY_MAX = 8192;
const TOP_RAIL_REBASE_DISTANCE = RUNWAY_TARGET;
const START_CAP_H = 0;
const DEFAULT_OVERSCAN_PX = 1200;
const DEFAULT_MAX_MOUNTED_ROWS = 60;
const LOW_END_MAX_MOUNTED_ROWS = 40;
const OVERSCAN_LOOKAHEAD_MS = 220;
const OVERSCAN_VELOCITY_EPSILON_PX_PER_MS = 0.05;
const MEASURE_EPSILON_PX = 0.5;
// Firefox quantizes scrollTop READS to integer CSS px while accepting fractional
// writes, so a fractional target (canvasH sums fractional measured heights) reads
// back up to 0.5px off. Any tighter write gate treats that as drift and rewrites
// every flush; each write fires a Firefox scrollend, whose flush rewrites again —
// a pin loop at frame rate that swallows all user scrolling.
const SCROLL_WRITE_EPSILON_PX = 1;
const PIN_EPSILON_PX = 4;
const MIN_THUMB_PX = 44;
const TOUCH_SCROLL_SETTLE_MS = 300;
const RESIZE_SESSION_SETTLE_MS = 250;
const SMOOTH_SCROLL_DURATION_MS = 420;
/** Maximum smooth-scroll travel measured in viewport heights before downgrading to an instant jump. */
export const SMOOTH_SCROLL_MAX_VIEWPORTS = 1.5;
const EMPTY_VIEWPORT_INSETS: ViewportInsets = {
  composerOverlayPx: 0,
  safeAreaBottomPx: 0
};

/** Minimal ResizeObserver subset used by the engine; caller owns disconnect on injected doubles. */
export interface ResizeObserverLike {
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

/** Factory for real or test ResizeObserver instances bound to the supplied callback. */
export type ResizeObserverFactory = (callback: ResizeObserverCallback) => ResizeObserverLike;

/** Engine phase in which a recovered error was raised. */
export type FeedEngineErrorPhase = 'estimate' | 'flush' | 'measure';

/** Static engine tuning knobs; all distances are px and callbacks are synchronous. */
export interface StaticFeedEngineOptions<TItem extends EngineItem> {
  estimateHeight?: ItemHeightEstimator<TItem>;
  maxMountedRows?: number;
  onDebugEvent?: (event: EngineDebugEvent) => void;
  /**
   * Recovered-error sink. When present, estimator failures fall back to the default
   * row estimate, and measurement/flush failures are reported instead of thrown —
   * one broken row degrades that row instead of freezing feed geometry. When absent
   * (tests, harness), errors propagate so invariant violations stay loud.
   */
  onError?: (error: unknown, phase: FeedEngineErrorPhase) => void;
  overscanPx?: number;
  resizeObserverFactory?: ResizeObserverFactory;
}

/** Creates an in-memory virtual feed engine; DOM is attached later through mount(). */
export function createStaticFeedEngine<TItem extends EngineItem>(
  options: StaticFeedEngineOptions<TItem> = {}
): FeedEngine<TItem> {
  return new StaticFeedEngine(options);
}

class StaticFeedEngine<TItem extends EngineItem> implements FeedEngine<TItem> {
  private scroller?: HTMLDivElement;
  private canvas?: HTMLDivElement;
  private items: TItem[] = [];
  private edges: EdgeState = { top: 'end', bottom: 'live' };
  private renderWindow: RenderWindow<TItem> = { items: [], epoch: 0, placeholderKeys: new Set() };
  private scrollState: ScrollState = {
    atBottom: true,
    distanceFromTop: 0,
    distanceFromBottom: 0,
    mode: 'AT_BOTTOM',
    userScrolling: false,
    viewportHeight: 0
  };
  private anchorSnapshot: AnchorSnapshot = { itemKey: '', offsetPx: 0 };
  private elements = new Map<FeedItemKey, HTMLElement>();
  private edgeElements = new Map<EdgeSide, HTMLElement>();
  private scrollbarTrack?: HTMLElement;
  private scrollbarThumb?: HTMLElement;
  private windowSubscribers = new Set<(window: RenderWindow<TItem>) => void>();
  private scrollSubscribers = new Set<(state: ScrollState) => void>();
  private layout: PrefixLayout = buildPrefixLayout([]);
  private layoutItems?: TItem[];
  private layoutContentWidth?: number;
  private layoutMeasurementVersion = -1;
  private layoutBuildHadEstimateError = false;
  private readonly heightCache = new HeightCache();
  private elementKeys = new WeakMap<Element, FeedItemKey>();
  private itemResizeObserver?: ResizeObserverLike;
  private scrollerResizeObserver?: ResizeObserverLike;
  private contentWidth = 1;
  private viewportH = 0;
  private viewportInsets: ViewportInsets = EMPTY_VIEWPORT_INSETS;
  private origin = 0;
  private canvasH = 0;
  private epoch = 0;
  private batchDepth = 0;
  private queuedBatchFlush?: FlushInput;
  private queuedBatchFollow?: FollowRule;
  private isFlushing = false;
  private pendingFlush = false;
  private retireTopRunwayOnNextFlush = false;
  private resizeFlushQueued = false;
  private pendingWidthSweep = false;
  private pendingResizeAnchor?: LockedAnchor;
  private lockedMeasurementAnchor?: LockedAnchor;
  private resizeSession?: LockedAnchor;
  private resizeSessionSettleTimer?: ReturnType<typeof setTimeout>;
  private programmaticScrollTop?: number;
  private scrollVelocityPxPerMs = 0;
  private lastScrollSample?: ScrollSample;
  private topRunwayCompressed = false;
  private touchScrolling = false;
  private touchContactActive = false;
  private pendingTouchRebase = false;
  private touchSettleTimer?: ReturnType<typeof setTimeout>;
  private thumbDragging = false;
  private thumbDragGrabOffsetPx = 0;
  private thumbDragPointerClientY = 0;
  private scrollbarTrackH = 0;
  private scrollbarTrackTop = 0;
  private scrollbarTrackMetricsDirty = false;
  private heightCachePrunePending = false;
  private writtenThumbH?: number;
  private writtenThumbTop?: number;
  private writtenItemTransforms = new Map<FeedItemKey, { el: HTMLElement; y: number }>();
  private smoothScroll?: SmoothScroll;
  private focusPinnedKey?: FeedItemKey;

  constructor(private readonly options: StaticFeedEngineOptions<TItem>) {}

  mount(scroller: HTMLDivElement, canvas: HTMLDivElement): void {
    this.scroller = scroller;
    this.canvas = canvas;
    this.contentWidth = Math.max(1, scroller.clientWidth || this.contentWidth);
    this.viewportH = Math.max(0, scroller.clientHeight || this.viewportH);
    this.itemResizeObserver = this.createResizeObserver(this.handleItemResize);
    this.scrollerResizeObserver = this.createResizeObserver(this.handleScrollerResize);
    this.scrollerResizeObserver?.observe(scroller);
    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
    this.scroller.addEventListener('wheel', this.handleUserScrollIntent, { passive: true });
    this.scroller.addEventListener('keydown', this.handleKeyDown, { passive: true });
    this.scroller.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.scroller.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.scroller.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
    this.scroller.addEventListener('pointerdown', this.handleUserScrollIntent, { passive: true });
    this.scroller.addEventListener('scrollend', this.handleScrollEnd, { passive: true });
    this.flush({ anchor: { kind: 'bottom' } });
  }

  unmount(): void {
    this.cancelSmoothScroll();
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    this.scroller?.removeEventListener('wheel', this.handleUserScrollIntent);
    this.scroller?.removeEventListener('keydown', this.handleKeyDown);
    this.scroller?.removeEventListener('touchstart', this.handleTouchStart);
    this.scroller?.removeEventListener('touchend', this.handleTouchEnd);
    this.scroller?.removeEventListener('touchcancel', this.handleTouchEnd);
    this.scroller?.removeEventListener('pointerdown', this.handleUserScrollIntent);
    this.scroller?.removeEventListener('scrollend', this.handleScrollEnd);
    this.scrollbarTrack?.removeEventListener('pointerdown', this.handleScrollbarTrackPointerDown);
    this.clearTouchSettleTimer();
    this.cancelResizeSession();
    this.itemResizeObserver?.disconnect();
    this.scrollerResizeObserver?.disconnect();
    this.scroller = undefined;
    this.canvas = undefined;
    this.itemResizeObserver = undefined;
    this.scrollerResizeObserver = undefined;
    this.batchDepth = 0;
    this.queuedBatchFlush = undefined;
    this.queuedBatchFollow = undefined;
    this.isFlushing = false;
    this.pendingFlush = false;
    this.retireTopRunwayOnNextFlush = false;
    this.resizeFlushQueued = false;
    this.pendingWidthSweep = false;
    this.pendingResizeAnchor = undefined;
    this.viewportH = 0;
    this.programmaticScrollTop = undefined;
    this.scrollVelocityPxPerMs = 0;
    this.lastScrollSample = undefined;
    this.topRunwayCompressed = false;
    this.touchScrolling = false;
    this.touchContactActive = false;
    this.pendingTouchRebase = false;
    this.thumbDragging = false;
    this.thumbDragGrabOffsetPx = 0;
    this.thumbDragPointerClientY = 0;
    this.scrollbarTrackH = 0;
    this.scrollbarTrackTop = 0;
    this.scrollbarTrackMetricsDirty = false;
    this.heightCachePrunePending = false;
    this.writtenThumbH = undefined;
    this.writtenThumbTop = undefined;
    this.focusPinnedKey = undefined;
    this.clearTouchSettleTimer();
    this.elements.clear();
    this.writtenItemTransforms.clear();
    this.edgeElements.clear();
    this.scrollbarTrack = undefined;
    this.scrollbarThumb = undefined;
    this.elementKeys = new WeakMap<Element, FeedItemKey>();
  }

  setWindow(items: TItem[], location: EngineLocation, edges: EdgeState): void {
    this.cancelSmoothScroll();
    const anchor = this.locationToAnchor(location);
    const mode: AnchorSolveMode = location.kind === 'item' ? 'scroll' : 'origin';
    if (this.resizeSession) this.resizeSession = { anchor, mode };
    this.lockedMeasurementAnchor = anchor.kind === 'item' ? { anchor, mode } : undefined;
    this.topRunwayCompressed = false;
    this.items = [...items];
    this.markItemsChanged();
    this.clearStaleFocusPin();
    this.edges = edges;
    this.epoch += 1;
    this.requestFlush({ anchor, mode });
  }

  prepend(items: TItem[]): void {
    // A block prepend must not strand an in-flight smooth scroll: pagination
    // fires while a jump animation approaches the top edge, and cancelling
    // here left the viewport wherever the animation happened to be. The
    // animation re-solves its anchor target every frame and re-bases across
    // the compensation write (see stepSmoothScroll), so it survives the
    // coordinate shift and still lands on target. The measurement lock stays
    // on the jump anchor for the same reason.
    const smoothScrollActive = this.smoothScroll !== undefined;
    const anchor = this.blockPrependAnchor();
    if (this.resizeSession) this.resizeSession = { anchor, mode: 'origin' };
    if (!smoothScrollActive) this.lockedMeasurementAnchor = { anchor, mode: 'origin' };
    this.topRunwayCompressed = false;
    this.items = [...items, ...this.items];
    this.markItemsChanged();
    this.clearStaleFocusPin();
    this.epoch += 1;
    this.requestFlush({ anchor, mode: 'origin' });
  }

  append(items: TItem[], follow: FollowRule): void {
    const anchor = this.anchorForAppendFollow(follow);
    if (anchor?.kind === 'bottom' && this.resizeSession) {
      this.resizeSession = { anchor: { kind: 'bottom' }, mode: 'origin' };
    }
    if (anchor?.kind === 'bottom' && this.lockedMeasurementAnchor) {
      // Any follow that resolves to present outranks a stale jump, restore, or
      // prepend lock. Conditional follows reach here only while already pinned.
      this.lockedMeasurementAnchor = undefined;
    }
    this.items = [...this.items, ...items];
    this.markItemsChanged();
    this.clearStaleFocusPin();
    this.epoch += 1;
    if (anchor && isSmoothFollow(follow)) {
      this.requestFlush({ anchor, behavior: 'smooth' });
    } else {
      // A follow anchor is an explicit reposition and outranks any in-flight
      // smooth scroll; a followless append lands below the viewport and must
      // not strand an active jump animation (mirrors prepend).
      if (anchor !== undefined) this.cancelSmoothScroll();
      this.requestFlush({ anchor });
    }
  }

  update(key: FeedItemKey, next: TItem): void {
    this.items = this.items.map((item) => (item.itemKey === key ? next : item));
    this.heightCache.deleteEstimate(key);
    this.markItemsChanged();
    this.clearStaleFocusPin();
    this.epoch += 1;
    this.requestFlush();
  }

  remove(key: FeedItemKey): void {
    this.items = this.items.filter((item) => item.itemKey !== key);
    this.markItemsChanged();
    this.clearStaleFocusPin();
    this.unregisterElement(key);
    this.heightCache.delete(key);
    this.epoch += 1;
    this.requestFlush();
  }

  invalidateItemHeight(key: FeedItemKey): void {
    this.heightCache.delete(key);
    const itemLoaded = this.items.some((item) => item.itemKey === key);
    if (!itemLoaded) return;

    const activeLock = this.resizeSession ?? this.lockedMeasurementAnchor;
    const anchor = activeLock?.anchor ?? this.environmentAnchor();
    const mode = activeLock?.mode ?? 'origin';
    // Keep this anchor through the DOM's following ResizeObserver delivery so
    // estimate and measured-height changes form one stable contextual reflow.
    this.lockedMeasurementAnchor = { anchor, mode };
    this.layoutItems = undefined;
    this.scrollbarTrackMetricsDirty = true;
    this.requestFlush({ anchor, mode });
  }

  batch(fn: () => void, follow?: FollowRule): void {
    this.batchDepth += 1;
    try {
      fn();
    } finally {
      if (follow !== undefined) {
        this.queuedBatchFollow = follow;
      }
      this.batchDepth -= 1;
      if (this.batchDepth === 0) {
        this.flushQueuedBatch();
      }
    }
  }

  scrollTo(location: EngineLocation, behavior: ScrollBehavior = 'instant'): void {
    if (behavior !== 'smooth') this.cancelSmoothScroll();
    const anchor = this.locationToAnchor(location);
    const mode: AnchorSolveMode = location.kind === 'item' ? 'scroll' : 'origin';
    if (this.resizeSession) this.resizeSession = { anchor, mode };
    this.lockedMeasurementAnchor = anchor.kind === 'item' ? { anchor, mode } : undefined;
    this.requestFlush({
      anchor,
      behavior,
      mode
    });
  }

  setEdges(patch: Partial<EdgeState>): void {
    const nextEdges = { ...this.edges, ...patch };
    if (this.edges.top !== 'end' && nextEdges.top === 'end') {
      this.retireTopRunwayOnNextFlush = true;
      this.topRunwayCompressed = false;
    }
    if (!usesTopRailBoundary(nextEdges.top)) {
      this.topRunwayCompressed = false;
    }
    this.edges = nextEdges;
    this.scrollbarTrackMetricsDirty = true;
    this.requestFlush();
  }

  setViewportInsets(patch: Partial<ViewportInsets>): void {
    const nextInsets = normalizeViewportInsets({ ...this.viewportInsets, ...patch });
    if (viewportInsetsEqual(nextInsets, this.viewportInsets)) return;

    const session = this.resizeSession;
    const anchor = session?.anchor ?? this.environmentAnchor();
    const mode = session?.mode ?? 'origin';
    this.lockedMeasurementAnchor = { anchor, mode };
    this.viewportInsets = nextInsets;
    this.scrollbarTrackMetricsDirty = true;
    this.requestFlush({ anchor, mode });
  }

  setFocusPinnedKey(key: FeedItemKey | null): void {
    const nextKey = key !== null && this.items.some((item) => item.itemKey === key)
      ? key
      : undefined;
    if (this.focusPinnedKey === nextKey) return;

    this.focusPinnedKey = nextKey;
    this.requestFlush();
  }

  getFocusPinnedKey(): FeedItemKey | undefined {
    return this.focusPinnedKey;
  }

  subscribeWindow(cb: (window: RenderWindow<TItem>) => void): Unsubscribe {
    this.windowSubscribers.add(cb);
    cb(this.renderWindow);
    return () => this.windowSubscribers.delete(cb);
  }

  getWindow(): RenderWindow<TItem> {
    return this.renderWindow;
  }

  registerItemElement(key: FeedItemKey, el: HTMLElement | null): void {
    if (el) {
      const previous = this.elements.get(key);
      if (previous && previous !== el) {
        this.itemResizeObserver?.unobserve(previous);
        this.elementKeys.delete(previous);
      }
      this.elements.set(key, el);
      this.elementKeys.set(el, key);
      this.syncItemObservation(key, el);
      this.writeItemTransform(key);
    } else {
      this.unregisterElement(key);
    }
  }

  registerEdgeElement(side: EdgeSide, el: HTMLElement | null): void {
    if (el) {
      this.edgeElements.set(side, el);
      this.writeEdgeTransform(side);
    } else {
      this.edgeElements.delete(side);
    }
  }

  registerScrollbarElements(track: HTMLElement | null, thumb: HTMLElement | null): void {
    if (this.scrollbarTrack && this.scrollbarTrack !== track) {
      this.scrollbarTrack.removeEventListener('pointerdown', this.handleScrollbarTrackPointerDown);
    }
    if (track && this.scrollbarTrack !== track) {
      track.addEventListener('pointerdown', this.handleScrollbarTrackPointerDown);
    }
    this.scrollbarTrack = track ?? undefined;
    this.scrollbarThumb = thumb ?? undefined;
    if (!this.scrollbarThumb) {
      this.writtenThumbH = undefined;
      this.writtenThumbTop = undefined;
      return;
    }

    this.readScrollbarTrackMetrics();
    this.scrollbarTrackMetricsDirty = false;
    this.writeScrollbarThumb(this.scroller?.scrollTop ?? 0);
  }

  beginThumbDrag(pointerClientY: number): void {
    if (!this.scrollbarThumb || !this.scrollbarTrack) return;

    this.cancelSmoothScroll();
    this.cancelResizeSession();
    if (this.lockedMeasurementAnchor) {
      this.emitDebug({
        type: 'anchor-release',
        cause: 'intent',
        scrollTop: this.scroller?.scrollTop ?? 0
      });
    }
    this.lockedMeasurementAnchor = undefined;
    this.programmaticScrollTop = undefined;
    this.readScrollbarTrackMetrics();
    this.scrollbarTrackMetricsDirty = false;

    const geometry = this.scrollbarGeometryForScrollTop(this.scroller?.scrollTop ?? 0);
    this.thumbDragging = true;
    this.thumbDragPointerClientY = pointerClientY;
    this.thumbDragGrabOffsetPx = clamp(
      pointerClientY - this.scrollbarTrackTop - geometry.top,
      0,
      geometry.height
    );
    this.updateScrollState();
    this.requestFlush({ thumbDrag: true });
  }

  updateThumbDrag(pointerClientY: number): void {
    if (!this.thumbDragging) return;

    this.thumbDragPointerClientY = pointerClientY;
    this.requestFlush({ thumbDrag: true });
  }

  endThumbDrag(): void {
    if (!this.thumbDragging) return;

    this.thumbDragging = false;
    this.thumbDragGrabOffsetPx = 0;
    this.requestFlush();
  }

  subscribeScrollState(cb: (state: ScrollState) => void): Unsubscribe {
    this.scrollSubscribers.add(cb);
    cb(this.scrollState);
    return () => this.scrollSubscribers.delete(cb);
  }

  getScrollState(): ScrollState {
    return this.scrollState;
  }

  getAnchorSnapshot(): AnchorSnapshot {
    return this.anchorSnapshot;
  }

  getDebugSnapshot(): EngineDebugSnapshot {
    return {
      canvasH: this.canvasH,
      contentWidth: this.contentWidth,
      edges: { ...this.edges },
      itemCount: this.items.length,
      lockedAnchor: this.lockedMeasurementAnchor ? anchorLabel(this.lockedMeasurementAnchor.anchor) : undefined,
      origin: this.origin,
      programmaticScrollTop: this.programmaticScrollTop,
      renderCount: this.renderWindow.items.length,
      renderFirstKey: this.renderWindow.items[0]?.itemKey,
      renderLastKey: this.renderWindow.items.at(-1)?.itemKey,
      viewportH: this.viewportH
    };
  }

  private readonly handleScroll = () => {
    this.updateScrollVelocity();
    this.releaseLockedAnchorForExternalScroll();
    const clampedToTop = this.touchScrolling
      ? this.pinTopEdgeDuringTouch()
      : (this.rebaseStartCapOriginAtHistoryStart(), this.rebaseTopRunwayNearRail(), this.clampTopRunwayScroll());
    if (!clampedToTop && this.topRunwayCompressed && this.distanceFromTopEdgeZone() > TOP_RAIL_REBASE_DISTANCE + MEASURE_EPSILON_PX) {
      this.topRunwayCompressed = false;
    }
    if (this.touchScrolling && !this.touchContactActive) {
      this.scheduleTouchSettle();
    }
    this.scrollState = this.readScrollState();
    this.flush();
  };

  private readonly handleUserScrollIntent = () => {
    this.cancelSmoothScroll();
    this.cancelResizeSession();
    if (this.lockedMeasurementAnchor) {
      this.emitDebug({
        type: 'anchor-release',
        cause: 'intent',
        scrollTop: this.scroller?.scrollTop ?? 0
      });
    }
    this.lockedMeasurementAnchor = undefined;
    this.programmaticScrollTop = undefined;
    this.emitDebug({
      type: 'user-intent',
      cause: 'intent',
      scrollTop: this.scroller?.scrollTop ?? 0
    });
  };

  private readonly handleKeyDown = (event: KeyboardEvent) => {
    if (!isScrollAffectingKey(event.key)) return;
    this.handleUserScrollIntent();
  };

  private readonly handleScrollbarTrackPointerDown = (event: PointerEvent) => {
    if (!this.scrollbarTrack || this.isScrollbarThumbEventTarget(event.target)) return;

    this.cancelSmoothScroll();
    this.cancelResizeSession();
    if (this.lockedMeasurementAnchor) {
      this.emitDebug({
        type: 'anchor-release',
        cause: 'intent',
        scrollTop: this.scroller?.scrollTop ?? 0
      });
    }
    this.lockedMeasurementAnchor = undefined;
    this.programmaticScrollTop = undefined;
    this.readScrollbarTrackMetrics();
    this.scrollbarTrackMetricsDirty = false;

    const clickY = event.clientY - this.scrollbarTrackTop;
    const geometry = this.scrollbarGeometryForScrollTop(this.scroller?.scrollTop ?? 0);
    if (clickY < geometry.top) {
      this.pageTrackClick('up');
    } else if (clickY > geometry.top + geometry.height) {
      this.pageTrackClick('down');
    }
  };

  private readonly handleTouchStart = () => {
    this.clearTouchSettleTimer();
    this.touchContactActive = true;
    this.touchScrolling = true;
    this.handleUserScrollIntent();
  };

  private readonly handleTouchEnd = () => {
    this.touchContactActive = false;
    this.scheduleTouchSettle();
  };

  private readonly handleScrollEnd = () => {
    this.scrollVelocityPxPerMs = 0;
    this.lastScrollSample = undefined;
    this.requestFlush();
    if (this.touchScrolling && !this.touchContactActive) {
      this.scheduleTouchSettle();
    }
  };

  private readonly handleItemResize: ResizeObserverCallback = (entries) => {
    let changed = false;
    // ResizeObserver delivers after layout, so clientWidth and entry size share a
    // layout generation; width stamps make observer delivery order irrelevant.
    const measuredWidth = Math.max(1, this.scroller?.clientWidth || this.contentWidth);
    for (const entry of entries) {
      // One malformed entry must not abort the rest of the batch: a skipped batch
      // leaves rows on estimated heights with no later delivery to correct them.
      try {
        const key = this.elementKeys.get(entry.target);
        if (!key) continue;
        if (this.isPlaceholderKey(key)) continue;

        const height = measuredBlockSize(entry);
        if (height <= 0) continue;

        const previous = this.heightCache.getMeasured(key, measuredWidth);
        if (previous === undefined || Math.abs(previous - height) > MEASURE_EPSILON_PX) {
          this.heightCache.setMeasured(key, height, measuredWidth);
          changed = true;
        }
      } catch (error: unknown) {
        if (!this.options.onError) throw error;
        this.options.onError(error, 'measure');
      }
    }

    if (changed) this.scheduleResizeFlush();
  };

  private readonly handleScrollerResize: ResizeObserverCallback = (entries) => {
    const entry = entries.find((candidate) => candidate.target === this.scroller);
    const width = Math.max(1, this.scroller?.clientWidth || entry?.contentRect.width || this.contentWidth);
    const height = Math.max(0, this.scroller?.clientHeight ?? entry?.contentRect.height ?? this.viewportH);
    const widthChanged = Math.abs(width - this.contentWidth) > MEASURE_EPSILON_PX;
    const heightChanged = Math.abs(height - this.viewportH) > MEASURE_EPSILON_PX;
    if (width <= 0 || (!widthChanged && !heightChanged)) return;

    const session = this.touchResizeSession();
    this.lockedMeasurementAnchor = { anchor: session.anchor, mode: session.mode };
    this.viewportH = height;
    if (widthChanged) {
      this.contentWidth = width;
      this.pendingWidthSweep = true;
      this.heightCachePrunePending = true;
    }
    this.scrollbarTrackMetricsDirty = true;
    this.scheduleResizeFlush({ anchor: session.anchor, mode: session.mode });
  };

  private requestFlush(input: FlushInput = {}) {
    if (this.batchDepth > 0) {
      this.queuedBatchFlush = mergeFlushInputs(this.queuedBatchFlush, input);
      return;
    }
    if (this.isFlushing) {
      this.pendingFlush = true;
      return;
    }
    this.flush(input);
  }

  private flushQueuedBatch() {
    const follow = this.queuedBatchFollow;
    const followAnchor =
      follow === undefined
        ? undefined
        : this.anchorForAppendFollow(follow);
    const queued = follow !== undefined && followAnchor
      ? { anchor: followAnchor, behavior: followBehavior(follow) }
      : this.queuedBatchFlush;

    this.queuedBatchFlush = undefined;
    this.queuedBatchFollow = undefined;
    if (queued) {
      this.flush(queued);
    }
  }

  private flush(input: FlushInput = {}) {
    this.isFlushing = true;
    try {
      this.runFlush(input);
    } catch (error: unknown) {
      // An escaped flush error would also kill every FOLLOWING flush's caller path
      // (RO microtask, scroll handler), freezing geometry while content keeps
      // changing — the overlap failure mode. Report and let the next flush retry.
      if (!this.options.onError) throw error;
      this.options.onError(error, 'flush');
    } finally {
      this.isFlushing = false;
      if (this.pendingFlush) {
        this.pendingFlush = false;
        this.flush({});
      }
    }
  }

  private runFlush(input: FlushInput = {}) {
    const previousLayout = this.layout;
    const previousOrigin = this.origin;
    const viewportH = this.scroller?.clientHeight ?? 0;
    const contentWidth = Math.max(1, this.scroller?.clientWidth ?? this.contentWidth);
    const scrollTop = this.scroller?.scrollTop ?? 0;
    const bottomInsetH = this.bottomInsetH();
    let anchor = input.anchor;
    let mode = input.mode;
    let activeAnchorIsLocked = anchor !== undefined && this.lockedMeasurementAnchor?.anchor === anchor;
    const canUseStoredLock = this.smoothScroll === undefined || input.anchor !== undefined;
    const contentWidthChanged = Math.abs(contentWidth - this.contentWidth) > MEASURE_EPSILON_PX;
    const viewportHeightChanged = Math.abs(viewportH - this.viewportH) > MEASURE_EPSILON_PX;
    if (contentWidthChanged || viewportHeightChanged) {
      if (!anchor && canUseStoredLock) {
        const session = this.touchResizeSession(previousLayout, previousOrigin, scrollTop, viewportH);
        anchor = session.anchor;
        mode = session.mode;
        activeAnchorIsLocked = true;
      } else {
        anchor ??= this.environmentAnchor(previousLayout, previousOrigin, scrollTop, viewportH);
        mode ??= 'origin';
      }
      if (canUseStoredLock) {
        this.lockedMeasurementAnchor = { anchor, mode };
        activeAnchorIsLocked = true;
      }
      this.contentWidth = contentWidth;
      this.viewportH = viewportH;
      if (contentWidthChanged) {
        this.pendingWidthSweep = true;
        this.heightCachePrunePending = true;
      }
    }
    if (contentWidthChanged || viewportHeightChanged) {
      this.scrollbarTrackMetricsDirty = true;
    }
    if (this.scrollbarTrackMetricsDirty) {
      this.readScrollbarTrackMetrics();
      this.scrollbarTrackMetricsDirty = false;
    }
    if (!anchor && canUseStoredLock && this.lockedMeasurementAnchor) {
      anchor = this.lockedMeasurementAnchor.anchor;
      mode = this.lockedMeasurementAnchor.mode;
      activeAnchorIsLocked = true;
    }
    anchor ??= this.currentAnchor(previousLayout, previousOrigin, scrollTop, viewportH);
    mode ??= 'origin';
    let activeAnchor: LayoutAnchor = anchor;
    const activeMode: AnchorSolveMode = mode;

    const bottomZoneH = this.edges.bottom === 'live' ? 0 : EDGE_ZONE_H;
    const shouldRetireTopRunway = this.retireTopRunwayOnNextFlush;

    const remapAnchor = () => {
      activeAnchor = this.anchorForCurrentLayout(activeAnchor, previousLayout, previousOrigin, scrollTop);
      if (activeAnchorIsLocked) {
        this.lockedMeasurementAnchor = { anchor: activeAnchor, mode: activeMode };
      }
    };

    const solveCurrentLayout = (): PlannedFlushGeometry => {
      const initialOrigin = initialOriginForWindow({
        contentH: this.layout.contentH,
        viewportH,
        bottomZoneH,
        bottomInsetH,
        hasOlder: this.edges.top !== 'end',
        runwayTarget: RUNWAY_TARGET,
        startCapH: START_CAP_H
      });
      let solved = solveAnchor({
        layout: this.layout,
        anchor: activeAnchor,
        mode: activeMode,
        origin: initialOrigin,
        scrollTop,
        viewportH,
        bottomZoneH,
        bottomInsetH
      });
      let rebaseShift =
        shouldRetireTopRunway
          ? initialOrigin - solved.origin
          : this.edges.top === 'end' || this.topRunwayCompressed
            ? 0
            : planRunwayRebase(solved.origin, {
                min: RUNWAY_MIN,
                target: RUNWAY_TARGET,
                max: RUNWAY_MAX
              });
      let pendingTouchRebase = false;
      if (this.touchScrolling && rebaseShift !== 0) {
        pendingTouchRebase = true;
        rebaseShift = 0;
      }
      if (rebaseShift !== 0) {
        solved = applyRebase(solved, rebaseShift);
      }

      const directScrollTop = this.directScrollTopForFlush(input, solved.canvasH, viewportH);
      if (directScrollTop !== undefined) {
        solved = {
          ...solved,
          scrollTop: clamp(directScrollTop, 0, Math.max(0, solved.canvasH - viewportH))
        };
      }
      return { pendingTouchRebase, rebaseShift, solved };
    };

    const planRenderWindow = (renderScrollTop: number): RenderWindow<TItem> => {
      const overscan = this.overscanPolicy(viewportH);
      const plan = renderWindowPlanForViewport({
        direction: overscan.direction,
        focusPinnedKey: this.focusPinnedKey,
        layout: this.layout,
        maxRows: overscan.maxRows,
        origin: this.origin,
        overscanAfterPx: overscan.afterPx,
        overscanBeforePx: overscan.beforePx,
        scrollTop: renderScrollTop,
        viewportH,
        overscanPx: overscan.basePx
      });
      return {
        items: plan.itemIndices.map((index) => this.items[index]),
        epoch: this.epoch,
        placeholderKeys: plan.placeholderKeys
      };
    };

    const publishRenderWindow = (nextRenderWindow: RenderWindow<TItem>) => {
      const shouldNotifyWindow = renderWindowChanged(this.renderWindow, nextRenderWindow);
      this.renderWindow = nextRenderWindow;
      this.syncRegisteredItemElements();
      if (shouldNotifyWindow) {
        this.windowSubscribers.forEach((subscriber) => subscriber(this.renderWindow));
      }
    };

    let planned: PlannedFlushGeometry = {
      pendingTouchRebase: false,
      rebaseShift: 0,
      solved: {
        canvasH: this.canvasH,
        origin: this.origin,
        scrollTop
      }
    };
    let shouldAnimateScroll = false;
    let renderScrollTop = scrollTop;

    // Placeholder flags never affect layout heights (§8.6's "no measured-height
    // feedback" severs the RO loop via non-observation; known measurements stay
    // authoritative either way), so one solve+plan pass is always consistent.
    const solveAndPlanRenderWindow = (): RenderWindow<TItem> => {
      this.layout = this.buildLayoutForItems(contentWidth);
      remapAnchor();
      planned = solveCurrentLayout();
      this.origin = planned.solved.origin;
      this.canvasH = planned.solved.canvasH;
      shouldAnimateScroll = this.shouldAnimateSmoothScroll(input.behavior, planned.solved.scrollTop, viewportH);
      renderScrollTop = shouldAnimateScroll ? scrollTop : planned.solved.scrollTop;
      return planRenderWindow(renderScrollTop);
    };

    const shouldSweepMountedRows = this.pendingWidthSweep;
    if (shouldSweepMountedRows) {
      this.pendingWidthSweep = false;
      this.measureMountedRowsAtWidth(contentWidth);
    }

    let nextRenderWindow = solveAndPlanRenderWindow();
    publishRenderWindow(nextRenderWindow);
    while (shouldSweepMountedRows && this.measureMountedRowsAtWidth(contentWidth)) {
      nextRenderWindow = solveAndPlanRenderWindow();
      publishRenderWindow(nextRenderWindow);
    }

    const anchorCritical = activeAnchor.kind === 'item' && (activeAnchorIsLocked || input.anchor?.kind === 'item')
      ? activeAnchor
      : undefined;
    if (this.measureAnchorCriticalRow(anchorCritical)) {
      nextRenderWindow = solveAndPlanRenderWindow();
      publishRenderWindow(nextRenderWindow);
    }

    this.pendingTouchRebase = planned.pendingTouchRebase;
    if (!planned.pendingTouchRebase) {
      this.retireTopRunwayOnNextFlush = false;
    }
    this.writeGeometry(renderScrollTop);
    if (input.behavior === 'smooth' && !shouldAnimateScroll) {
      this.cancelSmoothScroll();
    }
    if (shouldAnimateScroll) {
      this.startSmoothScroll(activeAnchor, activeMode);
    } else if (this.scroller && Math.abs(this.scroller.scrollTop - planned.solved.scrollTop) > SCROLL_WRITE_EPSILON_PX) {
      const fromScrollTop = this.scroller.scrollTop;
      this.scroller.scrollTop = planned.solved.scrollTop;
      this.programmaticScrollTop = planned.solved.scrollTop;
      this.emitDebug({
        type: 'scroll-write',
        cause: planned.rebaseShift !== 0 ? 'rebase' : activeAnchor.kind === 'bottom' ? 'bottom' : 'locked-anchor',
        fromScrollTop,
        rebaseShift: planned.rebaseShift,
        scrollTop,
        toScrollTop: planned.solved.scrollTop
      });
    }
    this.emitDebug({
      type: 'flush',
      cause: planned.rebaseShift !== 0 ? 'rebase' : 'flush',
      rebaseShift: planned.rebaseShift,
      scrollTop: planned.solved.scrollTop
    });
    if (this.heightCachePrunePending) {
      this.heightCache.prune({
        contentWidth: this.contentWidth,
        loadedKeys: this.items.map((item) => item.itemKey),
        mountedKeys: this.elements.keys()
      });
      this.heightCachePrunePending = false;
    }
    this.updateScrollState();
  }

  private currentAnchor(
    layout = this.layout,
    origin = this.origin,
    scrollTop = this.scroller?.scrollTop ?? 0,
    viewportH = this.scroller?.clientHeight ?? 0
  ): LayoutAnchor {
    if (this.scrollState.mode === 'AT_BOTTOM') return { kind: 'bottom' };
    return pickTopVisibleAnchor({ layout, origin, scrollTop, viewportH }) ?? this.offscreenItemAnchor(layout, origin, scrollTop) ?? { kind: 'bottom' };
  }

  private resizeAnchor(
    layout = this.layout,
    origin = this.origin,
    scrollTop = this.scroller?.scrollTop ?? 0,
    viewportH = this.scroller?.clientHeight ?? 0
  ): LayoutAnchor {
    if (this.scrollState.mode === 'AT_BOTTOM') return { kind: 'bottom' };
    return pickResizeAnchor({ layout, origin, scrollTop, viewportH, bottomInsetH: this.bottomInsetH() }) ?? this.currentAnchor(layout, origin, scrollTop, viewportH);
  }

  private anchorForCurrentLayout(
    anchor: LayoutAnchor,
    previousLayout: PrefixLayout,
    previousOrigin: number,
    scrollTop: number
  ): LayoutAnchor {
    if (anchor.kind === 'bottom' || this.layout.indexByKey.has(anchor.key)) return anchor;

    const previousIndex = previousLayout.indexByKey.get(anchor.key);
    if (previousIndex === undefined) return this.currentAnchor(this.layout);

    const replacementIndex = this.replacementIndexForRemovedAnchor(previousLayout, previousIndex);
    if (replacementIndex === undefined) return { kind: 'bottom' };

    // §7.2: the replacement keeps its own current viewport point, so rows at and
    // below the successor do not move; the removed row's space closes from above.
    const alpha = clamp(anchor.alpha, 0, 1);
    const replacementRow = previousLayout.rows[replacementIndex];
    const replacementViewportPoint =
      previousOrigin
      + previousLayout.prefix[replacementIndex]
      + alpha * replacementRow.height
      - scrollTop;
    return {
      kind: 'item',
      key: replacementRow.key,
      alpha,
      offsetPx: replacementViewportPoint
    };
  }

  private replacementIndexForRemovedAnchor(layout: PrefixLayout, removedIndex: number): number | undefined {
    for (let index = removedIndex + 1; index < layout.rows.length; index += 1) {
      if (this.layout.indexByKey.has(layout.rows[index].key)) return index;
    }
    for (let index = removedIndex - 1; index >= 0; index -= 1) {
      if (this.layout.indexByKey.has(layout.rows[index].key)) return index;
    }
    return undefined;
  }

  private environmentAnchor(
    layout = this.layout,
    origin = this.origin,
    scrollTop = this.scroller?.scrollTop ?? 0,
    viewportH = this.scroller?.clientHeight ?? this.viewportH
  ): LayoutAnchor {
    if (this.scrollState.mode === 'AT_BOTTOM') return { kind: 'bottom' };
    return this.resizeAnchor(layout, origin, scrollTop, viewportH);
  }

  private blockPrependAnchor(): LayoutAnchor {
    const scrollTop = this.scroller?.scrollTop ?? 0;
    const viewportH = this.scroller?.clientHeight ?? 0;
    const topVisible = pickTopVisibleAnchor({
      layout: this.layout,
      origin: this.origin,
      scrollTop,
      viewportH
    });
    if (!topVisible) return this.currentAnchor(this.layout, this.origin, scrollTop, viewportH);

    // Anchor the prepend at the topmost visible row's BOTTOM edge: a same-batch
    // decoration reflow of the boundary row (grouping chrome collapsing when the
    // new page joins an author run, §3) is then absorbed upward into the runway,
    // so every row below stays pixel-fixed (I1) instead of shifting by the
    // chrome delta.
    return {
      kind: 'item',
      key: topVisible.key,
      alpha: 1,
      offsetPx: topVisible.offsetPx + heightOf(this.layout, topVisible.key)
    };
  }

  private offscreenItemAnchor(layout: PrefixLayout, origin: number, scrollTop: number): LayoutAnchor | undefined {
    const first = layout.rows[0];
    const last = layout.rows.at(-1);
    if (!first || !last) return undefined;

    if (scrollTop < origin) {
      return {
        kind: 'item',
        key: first.key,
        alpha: 0,
        offsetPx: origin - scrollTop
      };
    }

    const contentBottom = origin + layout.contentH;
    if (scrollTop > contentBottom) {
      return {
        kind: 'item',
        key: last.key,
        alpha: 1,
        offsetPx: contentBottom - scrollTop
      };
    }

    return undefined;
  }

  private anchorForAppendFollow(follow: FollowRule): LayoutAnchor | undefined {
    if (follow === 'force' || follow === 'force-smooth') return { kind: 'bottom' };
    if ((follow === 'pin' || follow === 'pin-smooth') && this.scrollState.atBottom && this.edges.bottom === 'live') {
      return { kind: 'bottom' };
    }
    return undefined;
  }

  private locationToAnchor(location: EngineLocation): LayoutAnchor {
    if (location.kind === 'bottom') return { kind: 'bottom' };

    const alpha = location.align === 'start' ? 0 : location.align === 'center' ? 0.5 : 1;
    const viewportH = this.scroller?.clientHeight ?? 0;
    const offsetPx =
      location.offsetPx ??
      (location.align === 'start' ? 0 : location.align === 'center' ? viewportH / 2 : viewportH);
    return { kind: 'item', key: location.key, alpha, offsetPx };
  }

  private writeGeometry(scrollTopForThumb = this.scroller?.scrollTop ?? 0) {
    if (!this.canvas) return;
    this.assertAccountingIdentity();
    this.assertNoViewportPlaceholders(scrollTopForThumb);
    this.canvas.style.height = `${Math.max(0, this.canvasH)}px`;
    this.elements.forEach((_el, key) => this.writeItemTransform(key));
    this.edgeElements.forEach((_el, side) => this.writeEdgeTransform(side));
    this.writeScrollbarThumb(scrollTopForThumb);
  }

  private assertAccountingIdentity() {
    const bottomZoneH = this.edges.bottom === 'live' ? 0 : EDGE_ZONE_H;
    const bottomInsetH = this.bottomInsetH();
    const expectedCanvasH = this.origin + this.layout.contentH + bottomZoneH + bottomInsetH;
    if (Math.abs(this.canvasH - expectedCanvasH) <= MEASURE_EPSILON_PX) return;

    throw new Error(
      [
        'FeedEngine accounting identity failed:',
        `canvasH=${this.canvasH}`,
        `origin=${this.origin}`,
        `contentH=${this.layout.contentH}`,
        `bottomZoneH=${bottomZoneH}`,
        `bottomInsetH=${bottomInsetH}`,
        `expected=${expectedCanvasH}`
      ].join(' ')
    );
  }

  private assertNoViewportPlaceholders(scrollTop: number) {
    if (this.renderWindow.placeholderKeys.size === 0) return;

    const viewportBottom = scrollTop + (this.scroller?.clientHeight ?? this.viewportH);
    for (const key of this.renderWindow.placeholderKeys) {
      const index = this.layout.indexByKey.get(key);
      if (index === undefined) continue;

      const y = this.origin + this.layout.prefix[index];
      const height = this.layout.rows[index].height;
      if (y < viewportBottom && y + height > scrollTop) {
        throw new Error(
          [
            'FeedEngine placeholder viewport invariant failed:',
            `key=${key}`,
            `y=${y}`,
            `height=${height}`,
            `scrollTop=${scrollTop}`,
            `viewportH=${this.scroller?.clientHeight ?? this.viewportH}`
          ].join(' ')
        );
      }
    }
  }

  private writeItemTransform(key: FeedItemKey) {
    const el = this.elements.get(key);
    if (!el) return;
    const index = this.layout.indexByKey.get(key);
    if (index === undefined) return;

    const y = this.origin + this.layout.prefix[index];
    const roundedY = roundDevicePx(y);
    const written = this.writtenItemTransforms.get(key);
    if (!written || written.el !== el) {
      el.style.position = 'absolute';
      el.style.left = '0';
      el.style.right = '0';
    }
    if (!written || written.el !== el || written.y !== roundedY) {
      el.style.transform = `translateY(${roundedY}px)`;
      this.writtenItemTransforms.set(key, { el, y: roundedY });
    }
    if (this.isPlaceholderKey(key)) {
      el.style.height = `${this.layout.rows[index].height}px`;
    } else {
      removeStyleProperty(el, 'height');
    }
  }

  private writeEdgeTransform(side: EdgeSide) {
    const el = this.edgeElements.get(side);
    if (!el) return;

    const y = side === 'top' ? this.origin - EDGE_ZONE_H : this.origin + this.layout.contentH;
    const height =
      side === 'top'
        ? this.edges.top === 'end'
          ? START_CAP_H
          : EDGE_ZONE_H
        : this.edges.bottom === 'live'
          ? 0
          : EDGE_ZONE_H;
    el.style.position = 'absolute';
    el.style.left = '0';
    el.style.right = '0';
    el.style.height = `${height}px`;
    el.style.overflow = 'hidden';
    const dpr = globalThis.devicePixelRatio || 1;
    el.style.transform = `translateY(${Math.round(y * dpr) / dpr}px)`;
  }

  private writeScrollbarThumb(scrollTop: number) {
    const thumb = this.scrollbarThumb;
    if (!thumb) return;

    const geometry = this.thumbDragging
      ? this.pointerDrivenScrollbarGeometry()
      : this.scrollbarGeometryForScrollTop(scrollTop);
    const height = roundDevicePx(geometry.height);
    const top = roundDevicePx(geometry.top);

    thumb.style.position = 'absolute';
    thumb.style.left = '0';
    thumb.style.right = '0';
    if (this.writtenThumbH === undefined || Math.abs(this.writtenThumbH - height) > MEASURE_EPSILON_PX) {
      thumb.style.height = `${height}px`;
      this.writtenThumbH = height;
    }
    if (this.writtenThumbTop === undefined || Math.abs(this.writtenThumbTop - top) > MEASURE_EPSILON_PX) {
      thumb.style.transform = `translateY(${top}px)`;
      this.writtenThumbTop = top;
    }
  }

  private readScrollbarTrackMetrics() {
    const track = this.scrollbarTrack;
    if (!track) {
      this.scrollbarTrackH = 0;
      this.scrollbarTrackTop = 0;
      return;
    }

    const rect = track.getBoundingClientRect?.();
    this.scrollbarTrackH = Math.max(0, track.clientHeight || rect?.height || 0);
    this.scrollbarTrackTop = rect?.top ?? 0;
  }

  private directScrollTopForFlush(
    input: FlushInput,
    canvasH = this.canvasH,
    viewportH = this.viewportH
  ): number | undefined {
    if (this.thumbDragging || input.thumbDrag) {
      return this.scrollTopForThumbTop(this.pointerDrivenScrollbarGeometry(canvasH, viewportH).top, canvasH, viewportH);
    }
    if (input.scrollTop !== undefined) return input.scrollTop;
    return undefined;
  }

  private pointerDrivenScrollbarGeometry(
    canvasH = this.canvasH,
    viewportH = this.viewportH
  ): ScrollbarGeometry {
    const geometry = this.scrollbarGeometryForScrollTop(this.scroller?.scrollTop ?? 0, canvasH, viewportH);
    const top = clamp(
      this.thumbDragPointerClientY - this.scrollbarTrackTop - this.thumbDragGrabOffsetPx,
      0,
      geometry.maxTop
    );
    return { ...geometry, top };
  }

  private scrollbarGeometryForScrollTop(
    scrollTop: number,
    canvasH = this.canvasH,
    viewportH = this.viewportH
  ): ScrollbarGeometry {
    const trackH = Math.max(0, this.scrollbarTrackH);
    if (trackH <= 0) {
      return { height: 0, maxTop: 0, top: 0 };
    }

    if (canvasH <= viewportH) {
      return { height: trackH, maxTop: 0, top: 0 };
    }

    const height = Math.min(trackH, Math.max(MIN_THUMB_PX, (trackH * viewportH) / canvasH));
    const maxTop = Math.max(0, trackH - height);
    const maxScrollTop = Math.max(0, canvasH - viewportH);
    const top = maxScrollTop <= 0 ? 0 : maxTop * clamp(scrollTop, 0, maxScrollTop) / maxScrollTop;
    return { height, maxTop, top };
  }

  private scrollTopForThumbTop(
    thumbTop: number,
    canvasH = this.canvasH,
    viewportH = this.viewportH
  ): number {
    const geometry = this.scrollbarGeometryForScrollTop(this.scroller?.scrollTop ?? 0, canvasH, viewportH);
    if (geometry.maxTop <= MEASURE_EPSILON_PX || canvasH <= viewportH) return 0;
    return (thumbTop / geometry.maxTop) * Math.max(0, canvasH - viewportH);
  }

  private pageTrackClick(direction: TrackPageDirection) {
    if (!this.scroller) return;

    const pagePx = Math.max(0, this.viewportH - this.bottomInsetH());
    if (pagePx <= 0) return;

    const delta = direction === 'up' ? -pagePx : pagePx;
    this.requestFlush({ scrollTop: this.scroller.scrollTop + delta });
  }

  private isScrollbarThumbEventTarget(target: EventTarget | null): boolean {
    if (!this.scrollbarThumb || !target) return false;
    if (target === this.scrollbarThumb) return true;
    if (typeof this.scrollbarThumb.contains !== 'function') return false;

    try {
      // SAFETY: Event targets reaching contains() are DOM nodes in browsers; fake/non-node targets are caught below.
      return this.scrollbarThumb.contains(target as Node);
    } catch {
      return false;
    }
  }

  private createResizeObserver(callback: ResizeObserverCallback): ResizeObserverLike | undefined {
    if (this.options.resizeObserverFactory) return this.options.resizeObserverFactory(callback);
    if (typeof ResizeObserver === 'undefined') return undefined;
    return new ResizeObserver(callback);
  }

  private scheduleResizeFlush(input: { anchor?: LayoutAnchor; mode?: AnchorSolveMode } = {}) {
    this.pendingResizeAnchor = input.anchor
      ? { anchor: input.anchor, mode: input.mode ?? 'origin' }
      : this.pendingResizeAnchor;
    if (this.resizeFlushQueued) return;

    this.resizeFlushQueued = true;
    const run = () => {
      this.resizeFlushQueued = false;
      if (!this.scroller) {
        this.pendingResizeAnchor = undefined;
        return;
      }
      const locked = this.pendingResizeAnchor;
      this.pendingResizeAnchor = undefined;
      this.flush(locked ? { anchor: locked.anchor, mode: locked.mode } : {});
    };

    queueMicrotask(run);
  }

  private unregisterElement(key: FeedItemKey) {
    const previous = this.elements.get(key);
    if (!previous) return;
    this.itemResizeObserver?.unobserve(previous);
    this.elementKeys.delete(previous);
    this.elements.delete(key);
    this.writtenItemTransforms.delete(key);
  }

  private syncRegisteredItemElements() {
    this.elements.forEach((el, key) => {
      this.syncItemObservation(key, el);
      this.writeItemTransform(key);
    });
  }

  private syncItemObservation(key: FeedItemKey, el: HTMLElement) {
    if (this.isPlaceholderKey(key)) {
      this.itemResizeObserver?.unobserve(el);
      return;
    }

    this.itemResizeObserver?.observe(el);
  }

  private isPlaceholderKey(key: FeedItemKey): boolean {
    return this.renderWindow.placeholderKeys.has(key);
  }

  private clearStaleFocusPin() {
    if (this.focusPinnedKey === undefined) return;
    if (this.items.some((item) => item.itemKey === this.focusPinnedKey)) return;
    this.focusPinnedKey = undefined;
  }

  private releaseLockedAnchorForExternalScroll() {
    if (!this.scroller) return;

    const scrollTop = this.scroller.scrollTop;
    if (this.programmaticScrollTop !== undefined) {
      const isOwnScrollEvent = Math.abs(scrollTop - this.programmaticScrollTop) <= SCROLL_WRITE_EPSILON_PX;
      this.programmaticScrollTop = undefined;
      if (isOwnScrollEvent) {
        this.emitDebug({
          type: 'own-scroll',
          cause: 'own-scroll',
          scrollTop
        });
        return;
      }
    }

    // Browser clamp scrolls during canvas deflate have no preceding input event;
    // user intent cancels the resize session before its scroll event arrives.
    if (this.resizeSession) return;

    if (this.lockedMeasurementAnchor) {
      this.emitDebug({
        type: 'anchor-release',
        cause: 'external-scroll',
        scrollTop
      });
      this.lockedMeasurementAnchor = undefined;
    }
  }

  /** Captures the resize anchor once per session from pre-storm geometry; refreshes quiesce. */
  private touchResizeSession(
    layout = this.layout,
    origin = this.origin,
    scrollTop = this.scroller?.scrollTop ?? 0,
    viewportH = this.scroller?.clientHeight ?? this.viewportH
  ): LockedAnchor {
    if (!this.resizeSession) {
      const existingLock = this.lockedMeasurementAnchor;
      // An in-flight jump/restore (scroll-mode lock) outranks a fresh capture, matching
      // the pre-session behavior for resizes during jumps.
      this.resizeSession = existingLock?.mode === 'scroll'
        ? existingLock
        : { anchor: this.environmentAnchor(layout, origin, scrollTop, viewportH), mode: 'origin' };
    }
    this.scheduleResizeSessionSettle();
    return this.resizeSession;
  }

  private scheduleResizeSessionSettle() {
    if (this.resizeSessionSettleTimer !== undefined) {
      clearTimeout(this.resizeSessionSettleTimer);
    }
    this.resizeSessionSettleTimer = setTimeout(this.settleResizeSession, RESIZE_SESSION_SETTLE_MS);
  }

  private readonly settleResizeSession = () => {
    this.resizeSessionSettleTimer = undefined;
    if (!this.resizeSession) return;

    this.resizeSession = undefined;
    if (!this.scroller) return;
    this.requestFlush();
  };

  /** User input ends the session: the user's navigation outranks environment churn. */
  private cancelResizeSession() {
    if (this.resizeSessionSettleTimer !== undefined) {
      clearTimeout(this.resizeSessionSettleTimer);
      this.resizeSessionSettleTimer = undefined;
    }
    this.resizeSession = undefined;
  }

  private scheduleTouchSettle() {
    this.clearTouchSettleTimer();
    this.touchSettleTimer = setTimeout(this.settleTouchScrolling, TOUCH_SCROLL_SETTLE_MS);
  }

  private clearTouchSettleTimer() {
    if (this.touchSettleTimer === undefined) return;
    clearTimeout(this.touchSettleTimer);
    this.touchSettleTimer = undefined;
  }

  private readonly settleTouchScrolling = () => {
    this.clearTouchSettleTimer();
    if (!this.touchScrolling) return;

    this.touchScrolling = false;
    this.rebaseStartCapOriginAtHistoryStart();
    if (this.pendingTouchRebase || this.retireTopRunwayOnNextFlush) {
      this.requestFlush();
      return;
    }

    this.scrollState = this.readScrollState();
    this.scrollSubscribers.forEach((subscriber) => subscriber(this.scrollState));
  };

  // Post-measurement growth above the anchor is absorbed into origin so the reader's
  // position never jumps, but once the runway is retired at start of history that can
  // leave origin below the start cap — content above y=0 that scrollTop can never
  // reach. Repair on the next scroll (or touch settle): shifting origin, canvas, and
  // scrollTop together is visually a no-op, and the content top becomes reachable.
  private rebaseStartCapOriginAtHistoryStart(): void {
    if (!this.scroller || this.edges.top !== 'end' || this.layout.rows.length === 0) return;

    const shift = START_CAP_H - this.origin;
    if (shift <= MEASURE_EPSILON_PX) return;

    const fromScrollTop = this.scroller.scrollTop;
    this.origin += shift;
    this.canvasH = Math.max(0, this.canvasH + shift);
    // Grow the DOM canvas before moving scrollTop: the browser clamps scrollTop
    // against the current canvas height, and a clamped write loses part of the
    // shift — the content moves without the viewport and the reader sees a jump.
    this.writeGeometry();
    this.scroller.scrollTop = fromScrollTop + shift;
    this.programmaticScrollTop = this.scroller.scrollTop;
    // The self-inflicted scrollTop shift must not read as a fling on the next
    // velocity sample, or overscan flips into fast-scroll placeholder mode.
    this.scrollVelocityPxPerMs = 0;
    this.lastScrollSample = undefined;
    this.emitDebug({
      type: 'scroll-write',
      cause: 'top-boundary',
      fromScrollTop,
      rebaseShift: shift,
      scrollTop: fromScrollTop,
      toScrollTop: this.scroller.scrollTop
    });
  }

  private clampTopRunwayScroll(): boolean {
    if (!this.scroller || !usesTopRailBoundary(this.edges.top) || this.layout.rows.length === 0) return false;

    const minScrollTop = this.topEdgeScrollBoundary();
    if (this.scroller.scrollTop >= minScrollTop - MEASURE_EPSILON_PX) return false;

    const fromScrollTop = this.scroller.scrollTop;
    this.pinTopEdgeToRailStart();
    this.scroller.scrollTop = 0;
    this.programmaticScrollTop = 0;
    this.emitDebug({
      type: 'scroll-write',
      cause: 'top-boundary',
      fromScrollTop,
      rebaseShift: 0,
      scrollTop: fromScrollTop,
      toScrollTop: 0
    });
    return true;
  }

  private pinTopEdgeDuringTouch(): boolean {
    if (!this.scroller || !usesTopRailBoundary(this.edges.top) || this.layout.rows.length === 0) return false;

    const minScrollTop = this.topEdgeScrollBoundary();
    if (this.scroller.scrollTop >= minScrollTop - MEASURE_EPSILON_PX) return false;

    const targetOrigin = this.scroller.scrollTop + EDGE_ZONE_H;
    const shift = targetOrigin - this.origin;
    this.topRunwayCompressed = true;
    if (Math.abs(shift) <= MEASURE_EPSILON_PX) return true;

    this.origin = targetOrigin;
    this.canvasH = Math.max(0, this.canvasH + shift);
    this.writeGeometry();
    this.emitDebug({
      type: 'flush',
      cause: 'top-boundary',
      rebaseShift: shift,
      scrollTop: this.scroller.scrollTop
    });
    return true;
  }

  private rebaseTopRunwayNearRail() {
    if (!this.scroller || !usesGradualTopRailRebase(this.edges.top) || this.layout.rows.length === 0) return;

    const distance = this.distanceFromTopEdgeZone();
    if (distance > TOP_RAIL_REBASE_DISTANCE + MEASURE_EPSILON_PX) return;

    const progress = Math.max(0, Math.min(1, distance / TOP_RAIL_REBASE_DISTANCE));
    const targetOrigin = EDGE_ZONE_H + (RUNWAY_TARGET - EDGE_ZONE_H) * progress;
    const shift = targetOrigin - this.origin;
    if (Math.abs(shift) <= MEASURE_EPSILON_PX) {
      this.topRunwayCompressed = true;
      return;
    }

    const fromScrollTop = this.scroller.scrollTop;
    this.origin = targetOrigin;
    this.canvasH = Math.max(0, this.canvasH + shift);
    this.scroller.scrollTop = Math.max(0, fromScrollTop + shift);
    this.programmaticScrollTop = this.scroller.scrollTop;
    this.topRunwayCompressed = true;
    this.writeGeometry();
    this.emitDebug({
      type: 'scroll-write',
      cause: 'top-boundary',
      fromScrollTop,
      rebaseShift: shift,
      scrollTop: fromScrollTop,
      toScrollTop: this.scroller.scrollTop
    });
  }

  private topEdgeScrollBoundary(): number {
    const viewportH = this.scroller?.clientHeight ?? 0;
    const maxScrollTop = Math.max(0, this.canvasH - viewportH);
    return Math.min(maxScrollTop, Math.max(0, this.origin - EDGE_ZONE_H));
  }

  private pinTopEdgeToRailStart() {
    const shift = EDGE_ZONE_H - this.origin;
    if (Math.abs(shift) <= MEASURE_EPSILON_PX) {
      this.topRunwayCompressed = true;
      return;
    }

    this.origin = EDGE_ZONE_H;
    this.canvasH = Math.max(0, this.canvasH + shift);
    this.topRunwayCompressed = true;
    this.writeGeometry();
  }

  private distanceFromTopEdgeZone(): number {
    return (this.scroller?.scrollTop ?? 0) - (this.origin - EDGE_ZONE_H);
  }

  private emitDebug(input: Omit<EngineDebugEvent, 'canvasH' | 'edgeBottom' | 'edgeTop' | 'lockedAnchor' | 'origin'>) {
    this.options.onDebugEvent?.({
      ...input,
      canvasH: this.canvasH,
      edgeBottom: this.edges.bottom,
      edgeTop: this.edges.top,
      lockedAnchor: this.lockedMeasurementAnchor ? anchorLabel(this.lockedMeasurementAnchor.anchor) : undefined,
      origin: this.origin
    });
  }

  private updateScrollState() {
    this.scrollState = this.readScrollState();
    const scrollTop = this.scroller?.scrollTop ?? 0;
    const viewportH = this.scroller?.clientHeight ?? 0;
    const anchor = pickTopVisibleAnchor({
      layout: this.layout,
      origin: this.origin,
      scrollTop,
      viewportH
    });
    const anchorIndex = anchor ? this.layout.indexByKey.get(anchor.key) : undefined;
    const anchorItem =
      anchorIndex !== undefined
        ? this.items[anchorIndex]
        : this.renderWindow.items[0] ?? this.items[0];

    if (anchorItem) {
      this.anchorSnapshot = {
        itemKey: anchorItem.itemKey,
        id: anchorItem.id,
        offsetPx: anchor?.offsetPx ?? this.origin - scrollTop
      };
    }
    this.scrollSubscribers.forEach((subscriber) => subscriber(this.scrollState));
  }

  private readScrollState(): ScrollState {
    const viewportH = this.scroller?.clientHeight ?? 0;
    const scrollTop = this.scroller?.scrollTop ?? 0;
    const distanceFromTop = Math.max(0, scrollTop - this.origin);
    const distanceFromBottom = Math.max(0, this.canvasH - viewportH - scrollTop);
    const atBottom = distanceFromBottom <= PIN_EPSILON_PX;
    return {
      atBottom,
      distanceFromTop,
      distanceFromBottom,
      userScrolling: this.touchScrolling || this.thumbDragging,
      mode: atBottom && this.edges.bottom === 'live' ? 'AT_BOTTOM' : 'FREE',
      viewportHeight: viewportH
    };
  }

  private resolveItemHeight(item: TItem, contentWidth: number): number {
    try {
      return resolveHeight(item, contentWidth, this.heightCache, this.options.estimateHeight);
    } catch (error: unknown) {
      if (!this.options.onError) throw error;
      this.options.onError(error, 'estimate');
      this.layoutBuildHadEstimateError = true;
      return DEFAULT_ITEM_HEIGHT_ESTIMATE_PX;
    }
  }

  private buildLayoutForItems(contentWidth: number): PrefixLayout {
    const measurementVersion = this.heightCache.measurementVersion();
    if (
      this.layoutItems === this.items
      && this.layoutContentWidth === contentWidth
      && this.layoutMeasurementVersion === measurementVersion
    ) {
      return this.layout;
    }

    this.layoutBuildHadEstimateError = false;
    const layout = buildPrefixLayout(
      this.items.map((item) => ({
        key: item.itemKey,
        height: this.resolveItemHeight(item, contentWidth)
      }))
    );
    if (this.layoutBuildHadEstimateError) {
      // A fallback-height build must never be served from the memo: the stored
      // keys describe the last successful build while this.layout becomes this
      // failed one, and a reverting key (width bounce) would pair them. Drop the
      // keys so every flush retries the estimator until a build succeeds.
      this.layoutItems = undefined;
    } else {
      this.layoutItems = this.items;
      this.layoutContentWidth = contentWidth;
      this.layoutMeasurementVersion = measurementVersion;
    }
    return layout;
  }

  private markItemsChanged() {
    this.heightCachePrunePending = true;
    this.scrollbarTrackMetricsDirty = true;
  }

  private measureMountedRowsAtWidth(contentWidth: number): boolean {
    // A width-changing flush must not write stale or estimated geometry for a
    // mounted row when its current layout is measurable.
    let changed = false;
    for (const [key, el] of this.elements) {
      try {
        if (this.isPlaceholderKey(key)) continue;
        if (this.heightCache.getMeasured(key, contentWidth) !== undefined) continue;

        const height = measuredElementBlockSize(el);
        if (height <= 0) continue;

        this.heightCache.setMeasured(key, height, contentWidth);
        changed = true;
      } catch (error: unknown) {
        if (!this.options.onError) throw error;
        this.options.onError(error, 'measure');
      }
    }
    return changed;
  }

  private measureAnchorCriticalRow(anchor: LayoutAnchor | undefined): boolean {
    if (!anchor || anchor.kind !== 'item') return false;
    if (this.isPlaceholderKey(anchor.key)) return false;
    if (this.heightCache.getMeasured(anchor.key, this.contentWidth) !== undefined) return false;

    const el = this.elements.get(anchor.key);
    if (!el) return false;

    const height = measuredElementBlockSize(el);
    if (height <= 0) return false;

    this.heightCache.setMeasured(anchor.key, height, this.contentWidth);
    return true;
  }

  private shouldAnimateSmoothScroll(
    behavior: ScrollBehavior | undefined,
    targetScrollTop: number,
    viewportH: number
  ): boolean {
    if (behavior !== 'smooth' || !this.scroller || prefersReducedMotion()) return false;
    if (!withinSmoothScrollDistance(this.scroller.scrollTop, targetScrollTop, viewportH)) return false;
    return Math.abs(this.scroller.scrollTop - targetScrollTop) > SCROLL_WRITE_EPSILON_PX;
  }

  private bottomInsetH(): number {
    return this.viewportInsets.composerOverlayPx + this.viewportInsets.safeAreaBottomPx;
  }

  private startSmoothScroll(anchor: LayoutAnchor, mode: AnchorSolveMode) {
    if (!this.scroller) return;

    this.cancelSmoothScroll();
    this.smoothScroll = {
      anchor,
      mode,
      startScrollTop: this.scroller.scrollTop,
      lastWrittenScrollTop: this.scroller.scrollTop,
      startedAt: nowMs()
    };
    this.queueSmoothFrame();
  }

  private queueSmoothFrame() {
    if (!this.smoothScroll) return;
    this.smoothScroll.frame = requestFrame(this.stepSmoothScroll);
  }

  private readonly stepSmoothScroll = (timestamp: number) => {
    const smooth = this.smoothScroll;
    if (!smooth || !this.scroller) return;

    // Content mutations between frames (block-prepend compensation, runway
    // rebases) write scrollTop directly, shifting the coordinate space under
    // the animation. Re-base the eased start by the same delta so the glide
    // continues from the shifted position instead of hopping back to stale
    // coordinates; the target below is re-solved fresh every frame anyway.
    const drift = this.scroller.scrollTop - smooth.lastWrittenScrollTop;
    if (Math.abs(drift) > SCROLL_WRITE_EPSILON_PX) smooth.startScrollTop += drift;

    const targetScrollTop = this.targetScrollTopForAnchor(smooth.anchor, smooth.mode);
    if (targetScrollTop === undefined) {
      this.cancelSmoothScroll();
      return;
    }

    const elapsed = Math.max(0, timestamp - smooth.startedAt);
    const progress = prefersReducedMotion() ? 1 : Math.min(1, elapsed / SMOOTH_SCROLL_DURATION_MS);
    const eased = easeOutCubic(progress);
    const nextScrollTop = smooth.startScrollTop + (targetScrollTop - smooth.startScrollTop) * eased;
    this.scroller.scrollTop =
      progress >= 1 || Math.abs(targetScrollTop - nextScrollTop) <= SCROLL_WRITE_EPSILON_PX
        ? targetScrollTop
        : nextScrollTop;
    this.programmaticScrollTop = this.scroller.scrollTop;
    smooth.lastWrittenScrollTop = this.scroller.scrollTop;
    this.updateScrollVelocity(timestamp);
    this.scrollState = this.readScrollState();

    if (progress >= 1 || Math.abs(this.scroller.scrollTop - targetScrollTop) <= SCROLL_WRITE_EPSILON_PX) {
      this.smoothScroll = undefined;
      this.flush({ anchor: smooth.anchor, mode: smooth.mode });
      return;
    }

    this.flush();
    this.queueSmoothFrame();
  };

  private cancelSmoothScroll() {
    this.smoothScroll?.frame?.cancel();
    this.smoothScroll = undefined;
  }

  private targetScrollTopForAnchor(anchor: LayoutAnchor, mode: AnchorSolveMode): number | undefined {
    if (!this.scroller) return undefined;
    if (anchor.kind === 'item' && !this.layout.indexByKey.has(anchor.key)) return undefined;

    return solveAnchor({
      layout: this.layout,
      anchor,
      mode,
      origin: this.origin,
      scrollTop: this.scroller.scrollTop,
      viewportH: this.scroller.clientHeight,
      bottomZoneH: this.edges.bottom === 'live' ? 0 : EDGE_ZONE_H,
      bottomInsetH: this.bottomInsetH()
    }).scrollTop;
  }

  private updateScrollVelocity(timestamp = nowMs()) {
    if (!this.scroller) return;
    const scrollTop = this.scroller.scrollTop;
    const previous = this.lastScrollSample;
    this.lastScrollSample = { scrollTop, timestamp };
    if (!previous) {
      this.scrollVelocityPxPerMs = 0;
      return;
    }

    const elapsed = Math.max(1, timestamp - previous.timestamp);
    this.scrollVelocityPxPerMs = (scrollTop - previous.scrollTop) / elapsed;
  }

  private overscanPolicy(viewportH: number): OverscanPolicy {
    const basePx = Math.max(0, this.options.overscanPx ?? Math.max(DEFAULT_OVERSCAN_PX, viewportH));
    const direction = scrollDirection(this.scrollVelocityPxPerMs);
    const maxRows = normalizedMaxMountedRows(this.options.maxMountedRows);
    if (direction === 'idle' || basePx === 0) {
      return {
        afterPx: basePx,
        basePx,
        beforePx: basePx,
        direction,
        maxRows
      };
    }

    const lookaheadPx = Math.min(basePx * 2, Math.abs(this.scrollVelocityPxPerMs) * OVERSCAN_LOOKAHEAD_MS);
    const leadingPx = basePx + lookaheadPx;
    const trailingPx = basePx / 2;
    return {
      afterPx: direction === 'down' ? leadingPx : trailingPx,
      basePx,
      beforePx: direction === 'up' ? leadingPx : trailingPx,
      direction,
      maxRows
    };
  }
}

function measuredBlockSize(entry: ResizeObserverEntry): number {
  const borderBox = firstBoxSize(entry.borderBoxSize);
  if (borderBox?.blockSize) return borderBox.blockSize;
  if (entry.contentRect.height) return entry.contentRect.height;
  // SAFETY: ResizeObserverEntry.target is the observed element in this engine, registered only from HTMLElement refs.
  return (entry.target as HTMLElement).getBoundingClientRect?.().height ?? 0;
}

function measuredElementBlockSize(el: HTMLElement): number {
  return el.getBoundingClientRect?.().height ?? el.clientHeight ?? 0;
}

// Safari < 15.4 has ResizeObserver without borderBoxSize; older Firefox exposed it
// as a bare object. Both must reach the contentRect fallback instead of throwing —
// a throwing RO callback silently disables all height measurement.
function firstBoxSize(boxSize: ResizeObserverEntry['borderBoxSize'] | undefined): ResizeObserverSize | undefined {
  return boxSize?.[0];
}

type FlushInput = {
  anchor?: LayoutAnchor;
  behavior?: ScrollBehavior;
  mode?: AnchorSolveMode;
  scrollTop?: number;
  thumbDrag?: boolean;
};

type TrackPageDirection = 'down' | 'up';

interface ScrollbarGeometry {
  readonly height: number;
  readonly maxTop: number;
  readonly top: number;
}

interface SmoothScroll {
  readonly anchor: LayoutAnchor;
  frame?: AnimationFrameHandle;
  // Written by stepSmoothScroll: external scrollTop writes between frames
  // (block-prepend compensation, rebases) re-base startScrollTop by the drift
  // observed against lastWrittenScrollTop.
  lastWrittenScrollTop: number;
  readonly mode: AnchorSolveMode;
  startScrollTop: number;
  readonly startedAt: number;
}

interface AnimationFrameHandle {
  cancel(): void;
}

interface ScrollSample {
  readonly scrollTop: number;
  readonly timestamp: number;
}

interface OverscanPolicy {
  readonly afterPx: number;
  readonly basePx: number;
  readonly beforePx: number;
  readonly direction: RenderDirection;
  readonly maxRows: number;
}

interface PlannedFlushGeometry {
  readonly pendingTouchRebase: boolean;
  readonly rebaseShift: number;
  readonly solved: {
    readonly canvasH: number;
    readonly origin: number;
    readonly scrollTop: number;
  };
}

function normalizeViewportInsets(insets: ViewportInsets): ViewportInsets {
  return {
    composerOverlayPx: toInsetPx(insets.composerOverlayPx),
    safeAreaBottomPx: toInsetPx(insets.safeAreaBottomPx)
  };
}

function toInsetPx(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function viewportInsetsEqual(left: ViewportInsets, right: ViewportInsets): boolean {
  return (
    Math.abs(left.composerOverlayPx - right.composerOverlayPx) <= MEASURE_EPSILON_PX &&
    Math.abs(left.safeAreaBottomPx - right.safeAreaBottomPx) <= MEASURE_EPSILON_PX
  );
}

// §10: the user must never park in blank runway, and that must hold for ANY
// non-terminal top edge — with latency-keyed indicators (§14.3) a fast wheel
// burst outruns the delayed 'loading' write, so gating on loading/error alone
// leaves an unprotected 'idle' window the offscreen anchor then preserves.
function usesTopRailBoundary(state: EdgeState['top']): boolean {
  return state !== 'end';
}

// Gradual rail compression stays scoped to visible edge affordances: for an
// 'idle' edge near the top the hard boundary clamp alone protects the runway,
// without rewriting scrollTop on every ordinary near-top scroll event.
function usesGradualTopRailRebase(state: EdgeState['top']): boolean {
  return state === 'loading' || state === 'error';
}

function mergeFlushInputs(current: FlushInput | undefined, next: FlushInput): FlushInput {
  if (!current) return next;
  if (next.anchor) return next;
  return {
    ...current,
    ...next,
    behavior: next.behavior ?? current.behavior,
    thumbDrag: current.thumbDrag || next.thumbDrag
  };
}

function isSmoothFollow(follow: FollowRule): boolean {
  return follow === 'pin-smooth' || follow === 'force-smooth';
}

function followBehavior(follow: FollowRule): ScrollBehavior | undefined {
  return isSmoothFollow(follow) ? 'smooth' : undefined;
}

function roundDevicePx(value: number): number {
  const dpr = globalThis.devicePixelRatio || 1;
  return Math.round(value * dpr) / dpr;
}

function prefersReducedMotion(): boolean {
  return typeof globalThis.matchMedia === 'function'
    && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function withinSmoothScrollDistance(fromScrollTop: number, targetScrollTop: number, viewportH: number): boolean {
  if (viewportH <= 0) return false;
  return Math.abs(fromScrollTop - targetScrollTop) <= SMOOTH_SCROLL_MAX_VIEWPORTS * viewportH;
}

function isScrollAffectingKey(key: string): boolean {
  return (
    key === 'ArrowUp'
    || key === 'ArrowDown'
    || key === 'PageUp'
    || key === 'PageDown'
    || key === 'Home'
    || key === 'End'
    || key === ' '
    || key === 'Space'
    || key === 'Spacebar'
  );
}

function requestFrame(callback: FrameRequestCallback): AnimationFrameHandle {
  if (typeof globalThis.requestAnimationFrame === 'function') {
    const id = globalThis.requestAnimationFrame(callback);
    return { cancel: () => globalThis.cancelAnimationFrame?.(id) };
  }

  const timeout = setTimeout(() => callback(nowMs()), 16);
  return { cancel: () => clearTimeout(timeout) };
}

function nowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

function easeOutCubic(progress: number): number {
  const remaining = 1 - progress;
  return 1 - remaining * remaining * remaining;
}

function scrollDirection(velocityPxPerMs: number): RenderDirection {
  if (velocityPxPerMs > OVERSCAN_VELOCITY_EPSILON_PX_PER_MS) return 'down';
  if (velocityPxPerMs < -OVERSCAN_VELOCITY_EPSILON_PX_PER_MS) return 'up';
  return 'idle';
}

function normalizedMaxMountedRows(configured: number | undefined): number {
  const fallback = isLowEndDevice() ? LOW_END_MAX_MOUNTED_ROWS : DEFAULT_MAX_MOUNTED_ROWS;
  if (configured === undefined) return fallback;
  if (!Number.isFinite(configured)) return fallback;
  return Math.max(1, Math.floor(configured));
}

function isLowEndDevice(): boolean {
  // SAFETY: navigator.deviceMemory is a Chromium extension; the widened local shape only reads the optional field.
  const navigatorLike = globalThis.navigator as (Navigator & {
    readonly deviceMemory?: number;
  }) | undefined;
  if (!navigatorLike) return false;

  return (
    (typeof navigatorLike.deviceMemory === 'number' && navigatorLike.deviceMemory <= 4)
    || (typeof navigatorLike.hardwareConcurrency === 'number' && navigatorLike.hardwareConcurrency <= 4)
  );
}

function anchorLabel(anchor: LayoutAnchor): string {
  if (anchor.kind === 'bottom') return 'bottom';
  return `${anchor.key}@${anchor.alpha}:${Math.round(anchor.offsetPx)}`;
}

function renderWindowChanged<TItem extends EngineItem>(
  current: RenderWindow<TItem>,
  next: RenderWindow<TItem>
): boolean {
  if (current.epoch !== next.epoch || current.items.length !== next.items.length) return true;
  if (!feedItemKeySetsEqual(current.placeholderKeys, next.placeholderKeys)) return true;
  return current.items.some((item, index) => item.itemKey !== next.items[index]?.itemKey);
}

function feedItemKeySetsEqual(left: ReadonlySet<FeedItemKey>, right: ReadonlySet<FeedItemKey>): boolean {
  if (left.size !== right.size) return false;
  for (const key of left) {
    if (!right.has(key)) return false;
  }
  return true;
}

function removeStyleProperty(el: HTMLElement, property: string) {
  if (typeof el.style.removeProperty === 'function') {
    el.style.removeProperty(property);
    return;
  }

  // SAFETY: Test DOM style doubles may lack removeProperty but still expose mutable string-indexed style fields.
  delete (el.style as unknown as Record<string, string>)[property];
}

interface LockedAnchor {
  anchor: LayoutAnchor;
  mode: AnchorSolveMode;
}
